import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, Lock, ImagePlus, X, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';

interface Attachment {
  url: string;
  name: string;
}

interface TicketReplyFormProps {
  ticketId: string;
  isAdmin?: boolean;
  disabled?: boolean;
}

export function TicketReplyForm({ ticketId, isAdmin = false, disabled = false }: TicketReplyFormProps) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const replyMutation = useMutation({
    mutationFn: (data: { message: string; isInternal?: boolean; attachmentUrl?: string; attachmentName?: string }) => {
      const endpoint = isAdmin
        ? `/admin/support/tickets/${ticketId}/messages`
        : `/support/tickets/${ticketId}/messages`;
      return api.post(endpoint, data);
    },
    onSuccess: () => {
      setMessage('');
      setIsInternal(false);
      setAttachment(null);
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['admin-ticket', ticketId] });
      toast.success('Reply sent successfully');
    },
    onError: () => {
      toast.error('Failed to send reply');
    },
  });

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, and WebP images are allowed');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${import.meta.env.VITE_API_URL}/uploads/ticket-attachment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        setAttachment({
          url: result.data.attachmentUrl,
          name: result.data.attachmentName,
        });
      } else {
        toast.error(result.message || 'Upload failed');
      }
    } catch {
      toast.error('Upload failed');
    }

    setIsUploading(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = () => {
    setAttachment(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (message.trim().length < 2) {
      toast.error('Message is too short');
      return;
    }

    replyMutation.mutate({
      message: message.trim(),
      isInternal: isAdmin ? isInternal : undefined,
      attachmentUrl: attachment?.url,
      attachmentName: attachment?.name,
    });
  };

  if (disabled) {
    return (
      <div className="p-4 bg-leather rounded-lg border border-gold/20 text-center text-text-muted">
        This ticket is closed. No further replies can be added.
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Type your reply..."
          rows={4}
          maxLength={5000}
          className="w-full px-4 py-3 bg-leather border border-gold/20 rounded-lg text-cream placeholder-text-muted focus:border-gold focus:outline-none resize-none"
        />
        <div className="flex items-center justify-between mt-2">
          <div className="text-xs text-text-muted">
            {message.length}/5000
          </div>
          {isAdmin && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
                className="w-4 h-4 rounded border-gold/20 bg-leather text-purple-500 focus:ring-purple-500"
              />
              <span className="flex items-center gap-1 text-sm text-purple-400">
                <Lock className="w-3.5 h-3.5" />
                Internal note (not visible to customer)
              </span>
            </label>
          )}
        </div>
      </div>

      {/* Attachment */}
      <div>
        {attachment ? (
          <div className="flex items-center gap-3 p-3 bg-leather border border-gold/20 rounded-lg">
            <img
              src={attachment.url}
              alt={attachment.name}
              className="w-16 h-16 object-cover rounded"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-cream truncate">{attachment.name}</div>
              <div className="text-xs text-text-muted">Attached image</div>
            </div>
            <button
              type="button"
              onClick={removeAttachment}
              className="p-1 text-text-muted hover:text-hot transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileUpload}
              disabled={isUploading}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            />
            <div className={`flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-gold/30 rounded-lg text-sm text-text-secondary hover:border-gold/50 hover:text-cream transition-colors ${isUploading ? 'opacity-50' : ''}`}>
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <ImagePlus className="w-4 h-4" />
                  <span>Attach image (optional)</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={replyMutation.isPending || message.trim().length < 2}
        className={`flex items-center justify-center gap-2 px-6 py-3 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          isInternal
            ? 'bg-purple-600 text-white hover:bg-purple-500'
            : 'bg-gold text-leather hover:bg-gold-bright'
        }`}
      >
        {replyMutation.isPending ? (
          <>
            <div className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            {isInternal ? 'Add Internal Note' : 'Send Reply'}
          </>
        )}
      </button>
    </form>
  );
}
